import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Wrench, Square, Trash2, ExternalLink } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { EmployeeAvatar } from '@/components/EmployeeAvatar';

// ====== Service Call Punch Dialog ======
function ServiceCallPunchDialog({ sc, employees, onClose, onDeleted }) {
  const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';
  const [activePunches, setActivePunches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmpId, setSelectedEmpId] = useState("");
  const [punching, setPunching] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Membres de l'équipe du SC
  // 🆕 [6.1] Fix bug [object Object] : le modèle backend a employes:[{id, nom}]
  //   (cf. models_crm.py:402,542). On comparait sur em.employee_id (qui n'existe
  //   PAS) et le fallback passait l'objet entier comme nom → [object Object].
  const scMembers = (sc.employes || []).map(em => {
    if (em == null) return null;
    if (typeof em === 'string') {
      return employees.find(e => e.id === em) || { id: em, nom: em, prenom: '' };
    }
    const emp = employees.find(e => e.id === em.id);
    return emp || {
      id: em.id || '',
      nom: em.nom || em.id || '—',
      prenom: em.prenom || '',
      photo_url: em.photo_url || em.photo || '',
    };
  }).filter(Boolean);
  if (scMembers.length === 0 && sc.chef_equipe_id) {
    const chef = employees.find(e => e.id === sc.chef_equipe_id);
    if (chef) scMembers.push(chef);
  }

  const loadPunches = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/punch/current-sc/${sc.id}`);
      if (res.status === 200) {
        const data = res.data;
        setActivePunches(data.punches || []);
      }
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPunches(); }, [sc.id]);

  const handlePunchIn = async () => {
    if (!selectedEmpId) return;
    setPunching(true);
    try {
      const emp = employees.find(e => e.id === selectedEmpId);
      // 🔄 Phase A2.3 — Payload propre : on envoie service_call_id, le backend
      // dérive automatiquement bon_travail_id + force type_punch="service_call".
      const res = await axios.post(`${API_URL}/api/punch/in`, {
        employee_id: selectedEmpId,
        employee_nom: emp ? `${emp.prenom} ${emp.nom}`.trim() : selectedEmpId,
        service_call_id: sc.id,
      });
      if (res.status === 200) {
        toast.success("Service Call commencé !");
        await loadPunches();
        setSelectedEmpId("");
      } else {
        const err = res.data;
        toast.error(err.detail || "Erreur punch in");
      }
    } catch (e) {
      const err = e.response?.data;
      toast.error(err?.detail || "Erreur punch in");
    } finally {
      setPunching(false);
    }
  };

  const handlePunchOut = async (punchId) => {
    setPunching(true);
    try {
      const res = await axios.post(`${API_URL}/api/punch/out`, {
        punch_id: punchId
      });
      if (res.status === 200) {
        toast.success("Service Call terminé !");
        await loadPunches();
      } else {
        const err = res.data;
        toast.error(err.detail || "Erreur punch out");
      }
    } catch (e) {
      const err = e.response?.data;
      toast.error(err?.detail || "Erreur punch out");
    } finally {
      setPunching(false);
    }
  };

  const activePunchIds = new Set(activePunches.map(p => p.employee_id));

  const handleDeleteServiceCall = async () => {
    setDeleting(true);
    try {
      const res = await axios.delete(`${API_URL}/api/crm/service-calls/${sc.id}`);
      if (res.status === 200) {
        toast.success("Service Call supprimé");
        onDeleted();
      } else {
        const err = res.data;
        toast.error(err.detail || "Erreur lors de la suppression");
      }
    } catch (e) {
      const err = e.response?.data;
      toast.error(err?.detail || "Erreur réseau");
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5 text-yellow-600" />
            Service Call
          </DialogTitle>
          <DialogDescription>
            <strong>{sc.client_nom || sc.nom}</strong>
            {sc.chef_equipe_nom && ` · Équipe: ${sc.chef_equipe_nom}`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* 🆕 2026-05-14 — Bloc détails du SC (avant la section pointage) */}
          <div className="rounded-lg border border-yellow-200 bg-yellow-50/60 p-3 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-[10px] uppercase tracking-wide font-semibold text-yellow-700">Description</p>
                <p className="text-sm text-slate-800 whitespace-pre-wrap break-words mt-0.5">
                  {sc.description || <span className="italic text-slate-400">Aucune description</span>}
                </p>
              </div>
              {sc.dans_periode_garantie && (
                <span className="shrink-0 text-[10px] font-semibold bg-emerald-100 text-emerald-700 border border-emerald-300 rounded-full px-2 py-0.5">
                  Garantie
                </span>
              )}
              {sc.penalite_performance && (
                <span className="shrink-0 text-[10px] font-semibold bg-red-100 text-red-700 border border-red-300 rounded-full px-2 py-0.5">
                  Pénalité 85%
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2 text-[11px]">
              {sc.date_prevue && (
                <div>
                  <span className="text-slate-500">Date prévue : </span>
                  <span className="font-semibold text-slate-800">{sc.date_prevue}</span>
                </div>
              )}
              {sc.jours_apres_fin_travaux !== undefined && sc.jours_apres_fin_travaux !== null && (
                <div>
                  <span className="text-slate-500">Après fin chantier : </span>
                  <span className="font-semibold text-slate-800">{sc.jours_apres_fin_travaux} j</span>
                </div>
              )}
              {sc.dossier_id && (
                <div className="col-span-2">
                  <a
                    href={`/crm/projets/${sc.dossier_id}?tab=service-calls`}
                    className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`sc-link-dossier-${sc.id}`}
                  >
                    <ExternalLink className="h-3 w-3" />
                    Ouvrir le dossier client
                  </a>
                </div>
              )}
            </div>
            {Array.isArray(sc.photos) && sc.photos.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-wide font-semibold text-yellow-700 mb-1">
                  Photos ({sc.photos.length})
                </p>
                <div className="grid grid-cols-4 gap-1.5">
                  {sc.photos.slice(0, 8).map((ph, i) => {
                    const url = typeof ph === 'string' ? ph : (ph.url || ph.preview_url || '');
                    if (!url) return null;
                    return (
                      <a key={i} href={url} target="_blank" rel="noreferrer"
                         className="block aspect-square rounded overflow-hidden border border-yellow-200 hover:ring-2 hover:ring-yellow-400 transition">
                        <img src={url} alt={`SC ${i + 1}`} className="w-full h-full object-cover" />
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* 🆕 2026-05-23 — Section "Employés cédulés sur ce Service Call"
              Eliot demande de pouvoir voir QUI a été planifié sur le SC, pas
              seulement qui punche actuellement. La liste vient du SC lui-même
              (sc.employes) ; on retombe sur le chef d'équipe si aucun employé
              n'a été assigné explicitement. */}
          {scMembers.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                Employés cédulés ({scMembers.length})
              </p>
              <div className="flex flex-wrap gap-1.5" data-testid="sc-cedules-list">
                {scMembers.map(emp => {
                  const isPunched = activePunchIds.has(emp.id);
                  const isChef = emp.id === sc.chef_equipe_id;
                  return (
                    <div
                      key={emp.id}
                      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs border ${
                        isPunched
                          ? "bg-green-50 border-green-300 text-green-800"
                          : "bg-slate-50 border-slate-200 text-slate-700"
                      }`}
                      data-testid={`sc-cedule-${emp.id}`}
                    >
                      <EmployeeAvatar
                        photoUrl={emp.photo_url || emp.photo}
                        prenom={emp.prenom}
                        nom={emp.nom}
                        size="xs"
                      />
                      <span className="font-medium">
                        {isChef ? "👑 " : ""}{`${emp.prenom || ""} ${emp.nom || ""}`.trim() || emp.id}
                      </span>
                      {isPunched && (
                        <span className="ml-0.5 text-[9px] uppercase font-semibold tracking-wide">
                          en cours
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Section pointage */}
          {/* Employés actuellement punchés */}
          {activePunches.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">En cours</p>
              {activePunches.map(p => {
                const start = new Date(p.punch_in_time);
                const diff = Math.floor((new Date() - start) / 60000);
                const h = Math.floor(diff / 60), m = diff % 60;
                return (
                  <div key={p.id} className="flex items-center justify-between p-2.5 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <EmployeeAvatar photoUrl={p.employee_photo} prenom={(p.employee_nom || '?').split(' ')[0]} nom={(p.employee_nom || '').split(' ').slice(1).join(' ')} size="xs" />
                      <div>
                        <p className="text-sm font-semibold text-slate-800">{p.employee_nom}</p>
                        <p className="text-xs text-green-600 font-mono">{h > 0 ? `${h}h ` : ""}{m}min</p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => handlePunchOut(p.id)}
                      disabled={punching}
                      data-testid={`sc-punch-out-${p.id}`}
                    >
                      <Square className="h-3.5 w-3.5 mr-1" />
                      Terminer
                    </Button>
                  </div>
                );
              })}
            </div>
          )}

          {/* 🆕 2026-05-14 — UI nettoyée :
              On retire le bloc "Commencer le Service Call / Sélectionner un employé"
              puisque l'employé est défini à la création du SC et que le pointage
              se fait depuis l'interface terrain de l'employé (Mon pointage).
              On garde uniquement la liste "En cours" (pour intervenir au besoin)
              + le bouton Supprimer dans le footer. */}
          {loading && (
            <p className="text-xs text-center text-slate-400 py-2">Chargement...</p>
          )}
          {!loading && activePunches.length === 0 && (
            <p className="text-xs text-center text-slate-400 py-1">Aucun employé pointé sur ce Service Call</p>
          )}
        </div>

        <DialogFooter className="flex justify-between sm:justify-between">
          {!confirmDelete ? (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setConfirmDelete(true)}
              data-testid="sc-delete-btn"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Supprimer
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDeleteServiceCall}
                disabled={deleting}
                data-testid="sc-delete-confirm-btn"
              >
                {deleting ? "Suppression..." : "Confirmer"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConfirmDelete(false)}
                data-testid="sc-delete-cancel-btn"
              >
                Annuler
              </Button>
            </div>
          )}
          <Button variant="outline" onClick={onClose}>Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ServiceCallPunchDialog;
